import { useEffect, useRef } from "react";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export type CtaLocation =
  | "hero_primary"
  | "hero_install"
  | "hero_sample_report"
  | "sticky_desktop"
  | "sticky_mobile"
  | "pricing_free"
  | "pricing_starter"
  | "pricing_pro"
  | "pricing_enterprise"
  | "closing_cta"
  | "examples_sample_report"
  | "how_it_works"
  | "nav_signup"
  | "nav_login";

export function trackCtaClick(location: CtaLocation): void {
  window.gtag?.("event", "landing_cta_click", { cta_location: location });
}

export function trackSignupStarted(location: CtaLocation): void {
  window.gtag?.("event", "signup_started", { cta_location: location, source_page: "landing" });
}

export function trackFaqClick(questionId: string, questionText: string): void {
  window.gtag?.("event", "landing_faq_click", { faq_question: questionId, question_text: questionText });
}

export function trackSectionView(sectionId: string): void {
  window.gtag?.("event", "landing_section_view", { section_name: sectionId });
}

export function trackVideoPlay(videoId: string, videoTitle: string): void {
  window.gtag?.("event", "landing_video_play", { video_id: videoId, video_title: videoTitle });
}

/**
 * Fires `trackSectionView` once when the referenced element first enters the
 * viewport (at 30 % visibility). Disconnects the observer after firing and
 * cleans up on unmount.
 */
export function useSectionViewTracking(
  sectionId: string,
  ref: React.RefObject<HTMLElement | null>,
): void {
  const firedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !firedRef.current) {
          firedRef.current = true;
          trackSectionView(sectionId);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [sectionId, ref]);
}
